import { ORDERING_ELSEWHERE, UNCONFIRMED_BANK, UNCONFIRMED_CARD } from "../../src/lib/checkout-copy.ts";
import { contactPhrase, orContact } from "./brand.ts";
import type { Brand } from "./brand.ts";
import { CONTACT_LIMITS } from "../../src/lib/contact.ts";
import { money } from "../../src/lib/money.ts";
import type { TokenizerConfig } from "../../src/payments/gateway.ts";
import type { PaymentStatus } from "../../src/payments/machine.ts";

// Pure helpers for the checkout page (web/checkout.ts). The page never sends an amount: the server prices the order
// from the stored request and the quote key proves which prices the page showed.

export type Fulfillment = "pickup" | "ship";
export type Method = "card" | "bank";

export interface CheckoutForm {
  name: string;
  email: string;
  phone: string;
  business: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export const EMPTY_FORM: CheckoutForm = { name: "", email: "", phone: "", business: "", line1: "", line2: "", city: "", state: "", zip: "" };

export interface Quote {
  open: boolean;
  // The request's storefront (src/api/checkout.ts): its name for the bank authorization, and its brand for the page.
  storefrontName?: string;
  brand?: Brand;
  tokenizer: TokenizerConfig | null;
  sheetUrl: string | null;
  product: "apparel" | "transfers" | "own";
  lines: string[];
  placements: string[];
  // Each print's tier with its two prices at list, one per placement; empty for transfers.
  prints: { label: string; bankCents: number; cardCents: number }[];
  count: number;
  // Blank shipping inside both totals as the two prices (0 and 0 once waived) and the goods total that makes it free;
  // null for transfers and the buyer's own garments.
  blankShipping?: { bankCents: number; cardCents: number; freeAtCents: number } | null;
  pickup: { bankCents: number; cardCents: number };
  ship: { bankCents: number; cardCents: number };
  // Sales tax inside both totals (decision 23): the rate and the cents on pickup and on shipped; absent when the
  // shop charges none, and the page then shows no tax line.
  tax?: { rateBp: number; pickupCents: number; shipCents: number } | null;
  // The quantity tier and what it saves on each fulfillment's two totals, or null below the first tier.
  discount: { pct: number; atCount: number; pickup: { bankCents: number; cardCents: number }; ship: { bankCents: number; cardCents: number } } | null;
  quoteKey: string;
  // A reorder: the quantities the buyer may change (every color with the sizes it comes in now, or the transfers'
  // count), and what the reorder left out when it was made.
  edit?: QuantityEdit;
  dropped?: string[];
  // A staff pay link: the customer's name and the pickup or shipping staff picked, and the link's last day.
  prefill?: { name: string };
  fulfillment?: Fulfillment;
  expiresOn?: string;
}

export interface QuantityEdit {
  colors: { id: string; label: string; sizes: string[]; quantities: Record<string, number> }[];
  transfer: { quantity: number; max: number } | null;
}

// What a reorder's page sends as its quantities: per color id, size to count; or the transfers' count.
export type Asked = Record<string, Record<string, number>> | { transfer: number };

export function askedFrom(e: QuantityEdit): Asked {
  if (e.transfer) return { transfer: e.transfer.quantity };
  return Object.fromEntries(e.colors.map((c) => [c.id, { ...c.quantities }]));
}

// The count a quantity box holds: whole numbers from 0 (1 for transfers) to max; anything else reads as 0.
export function boxCount(value: string, max: number): number {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n > 0 ? Math.min(n, max) : 0;
}

export function askedTotal(a: Asked): number {
  if ("transfer" in a && typeof a.transfer === "number") return a.transfer;
  return Object.values(a as Record<string, Record<string, number>>).reduce((n, q) => n + Object.values(q).reduce((x, y) => x + y, 0), 0);
}

// The receipt's "Order this again" link: /checkout/reorder#o=<the order's reorder token>.
export function reorderTokenFrom(path: string, hash: string): string | null {
  if (path.split("/")[2] !== "reorder") return null;
  const m = /^#o=([A-Za-z0-9_-]{32})$/.exec(hash);
  return m ? m[1]! : null;
}

export interface Placed {
  orderNumber: number | null;
  method: Method;
  amountCents: number;
  // A new order is authorized (card), pending or settled (bank); a repeat of the same checkout (a reload, "Check my
  // order") answers where its payment stands now, which can be any status.
  status: PaymentStatus | null;
  last4: string | null;
}

// The designer's Sent view hands the buyer's own details to the checkout in this tab's sessionStorage, read once and
// removed. The share link never carries them.
export const prefillKey = (token: string) => `pressroom.checkout.${token}`;

type Contact = Pick<CheckoutForm, "name" | "email" | "phone" | "business">;

export function savePrefill(store: Pick<Storage, "setItem"> | null, token: string, c: Contact): void {
  try {
    store?.setItem(prefillKey(token), JSON.stringify({ name: c.name, email: c.email, phone: c.phone, business: c.business }));
  } catch {
    // Storage blocked: the buyer types the details again.
  }
}

export function takePrefill(store: Pick<Storage, "getItem" | "removeItem"> | null, token: string): Partial<Contact> {
  try {
    const raw = store?.getItem(prefillKey(token)) ?? null;
    store?.removeItem(prefillKey(token));
    if (!raw) return {};
    const v = JSON.parse(raw) as Record<string, unknown>;
    if (["name", "email", "phone", "business"].some((k) => typeof v[k] !== "string" || (v[k] as string).length > CONTACT_LIMITS[k as keyof Contact])) return {};
    return { name: v.name as string, email: v.email as string, phone: v.phone as string, business: v.business as string };
  } catch {
    return {};
  }
}

export function addressErrors(f: CheckoutForm, fulfillment: Fulfillment): Partial<Record<keyof CheckoutForm, string>> {
  if (fulfillment !== "ship") return {};
  const e: Partial<Record<keyof CheckoutForm, string>> = {};
  if (!f.line1.trim()) e.line1 = "Enter a street address.";
  if (!f.city.trim()) e.city = "Enter a city.";
  if (!f.state.trim()) e.state = "Enter a state.";
  if (!f.zip.trim()) e.zip = "Enter a ZIP code.";
  return e;
}

export const CARD_NOTE = "Your card is authorized now and charged when we approve your artwork. If we cannot print it, the hold is released.";

// The ACH debit authorization, with the exact amount and the storefront taking it, shown above Place order when paying
// by bank. The name comes from the checkout quote (the request's own storefront), never from the page's brand fetch.
// Bank payment is offered only when the quote names whose account debits it, and never on a Square account (card only,
// feat/square-gateway); null (no bank option) otherwise.
export function bankConsentFor(q: Pick<Quote, "storefrontName"> & { tokenizer?: TokenizerConfig | null }, amountCents: number): string | null {
  if (q.tokenizer?.kind === "square") return null;
  const name = q.storefrontName?.trim();
  return name ? bankConsent(amountCents, name) : null;
}

export function bankConsent(amountCents: number, storefrontName: string): string {
  return `By placing this order you authorize ${storefrontName} to debit ${money(amountCents)} from this account once. If the debit is returned, the order is cancelled.`;
}

// The unknown-outcome words live in src/lib/checkout-copy.ts, so the server's payment_unconfirmed answer says the same.
export { UNCONFIRMED_BANK, UNCONFIRMED_CARD };

// What Place order says after a failed answer. `unknown` is true when the outcome cannot be known from the answer:
// the page then offers "Check my order" (the same checkout, method and token) instead of Place order.
export function placeError(e: { status: number; code: string; message: string }, method: Method): { text: string; unknown: boolean } {
  if (e.code === "payment_unconfirmed" || e.status === 0 || (e.status >= 500 && e.code !== "payments_closed")) {
    return { text: method === "card" ? UNCONFIRMED_CARD : UNCONFIRMED_BANK, unknown: true };
  }
  // Another tab or window of this request is placing its order: this tab sent nothing, so Place order stays (its
  // token was never used), with the server's words.
  if (e.code === "checkout_in_progress" && e.message === ORDERING_ELSEWHERE) return { text: e.message, unknown: false };
  // An earlier click of this checkout whose answer was lost is still placing this order: the server's words say so.
  if (e.code === "checkout_in_progress") return { text: e.message, unknown: true };
  return { text: e.message, unknown: false };
}

// The checkout id lives in this tab's sessionStorage, so a reload after an unknown outcome is still the same checkout
// (the server then answers the order it made, or that the payment is in flight) and never a second charge.
export const checkoutIdKey = (token: string) => `pressroom.checkout-id.${token}`;

export function readCheckoutId(store: Pick<Storage, "getItem"> | null, token: string): string | null {
  try {
    const v = store?.getItem(checkoutIdKey(token)) ?? null;
    return v && /^[0-9a-f-]{36}$/i.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function keepCheckoutId(store: Pick<Storage, "setItem" | "removeItem"> | null, token: string, id: string | null): void {
  try {
    if (id) store?.setItem(checkoutIdKey(token), id);
    else store?.removeItem(checkoutIdKey(token));
  } catch {
    // Storage blocked: the id lives only as long as the page.
  }
}

export const TOKENIZE_TIMEOUT_MS = 20000;
export const tokenizeTimeout = () => orContact("The payment form did not answer. Check your details and try again");

// The hosted fields' tokenize() with a time limit, so a stuck gateway script never leaves Place order spinning.
export function tokenizeWithin<T>(work: Promise<T>, ms = TOKENIZE_TIMEOUT_MS): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error(tokenizeTimeout())), ms); });
  return Promise.race([work, late]).finally(() => clearTimeout(timer));
}

const call = () => {
  const c = contactPhrase();
  return c ? `Reply to your email or ${c} with any questions.` : "Reply to your email with any questions.";
};

export function placedView(r: Placed): { title: string; lines: string[] } {
  if (r.orderNumber === null) return { title: "Thanks", lines: ["We will be in touch."] };
  const end = r.last4 ? ` ending ${r.last4}` : "";
  const total = `Total: ${money(r.amountCents)}`;
  const placed = `Order #${r.orderNumber} placed`;
  const closed = `Order #${r.orderNumber}`;
  const account = r.method === "card" ? `your card${end}` : `your bank account${end}`;
  // Money that went back, or never arrived, whatever the method.
  if (r.status === "refunding") return { title: closed, lines: [total, `We are refunding this payment to ${account}. We email you when it is done.`, call()] };
  if (r.status === "refunded") return { title: closed, lines: [total, `This payment was refunded to ${account}. It can take 5 to 10 business days to show up.`, call()] };
  if (r.method === "card") {
    // Released is said only once the gateway confirmed it; while the void is in flight, it is being released.
    if (r.status === "voiding") return { title: closed, lines: [total, `We are releasing the hold on your card${end}. We email you when it is done.`, call()] };
    if (r.status === "voided") return { title: closed, lines: [total, `The hold on your card${end} is released, so nothing was charged.`, call()] };
    if (r.status === "declined" || r.status === "failed") return { title: closed, lines: [total, "Your card was not charged.", call()] };
    if (r.status === "captured") return { title: placed, lines: [total, `Paid by card${end}.`, "We email you if anything needs a change."] };
    return { title: placed, lines: [total, `Card${end} on hold until approval.`, "Your card is charged when we approve your artwork. We email you if anything needs a change."] };
  }
  if (r.status === "returned") return { title: closed, lines: [total, "Your bank returned this payment, so the order is cancelled. Nothing will be printed.", call()] };
  if (r.status === "declined" || r.status === "failed") return { title: closed, lines: [total, "The bank payment did not go through, so the order is cancelled. Nothing will be printed.", call()] };
  if (r.status === "settled") return { title: placed, lines: [total, `Paid by bank${end}.`, "We email you if anything needs a change."] };
  return { title: placed, lines: [total, "Bank payment pending.", "We start on your order once it clears, usually in a few business days. We email you if anything needs a change."] };
}

// The share page's line for a request ordered in the last few minutes (ruling R35): the order, its one amount and
// how it was paid, as the placed view says it.
export function orderedLine(r: Placed & { orderNumber: number }): string {
  const v = placedView(r);
  return `Order #${r.orderNumber}. ${v.lines[0]}. ${v.lines[1]}`;
}

// What the page does after a clear failure. A declined payment, a one-time token the gateway had already seen
// (token_used: the details are entered again, ruling R13) or a check that found the attempt failed mount the payment
// fields again. payment_limit (this checkout tried too often) and payments_closed forget the stored checkout id, so a
// reload starts a new checkout.
export function afterFailure(code: string, wasChecking: boolean): { refill: boolean; forgetCheckout: boolean } {
  const forgetCheckout = code === "payment_limit" || code === "payments_closed";
  return { refill: !forgetCheckout && (code === "payment_declined" || code === "token_used" || wasChecking), forgetCheckout };
}

export function checkoutBody(o: {
  token: string; checkoutId: string; quoteKey: string; method: Method; paymentToken: string; fulfillment: Fulfillment; form: CheckoutForm; honeypot: string;
  asked?: Asked | null;
}): Record<string, unknown> {
  const f = o.form;
  return {
    action: "checkout", token: o.token, checkoutId: o.checkoutId, quoteKey: o.quoteKey, method: o.method, paymentToken: o.paymentToken,
    fulfillment: o.fulfillment, ...(o.asked ? { quantities: o.asked } : {}),
    ...(o.fulfillment === "ship" ? { shipAddress: { line1: f.line1, line2: f.line2, city: f.city, state: f.state, zip: f.zip } } : {}),
    contact: { name: f.name, email: f.email, phone: f.phone, business: f.business },
    website: o.honeypot,
  };
}
